import React, { useCallback } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { AccordionSection, Button, H2, H4, Text } from 'components';
import { theme, toRem } from 'design';
import { routeConstants, routeForPaymentDetail } from 'routes';
import { getCompletedPaymentSetRequest } from 'store/Collections';
import { AcceptancePaymentSetStatus, DisbursementMethod, ITipAcceptancePaymentSet } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateForUILongFormat } from 'utils/date';
import { useWindowSize } from '../../../hooks';
import { PayTipsTooltip } from './styles';

interface IAcceptancePaymentSetProps {
  acceptanceBalance?: number;
  acceptancePaymentSet?: ITipAcceptancePaymentSet;
  isLoading?: boolean;
  makePaymentUrl?: string;
  summaryType?: string;
}

const InfoContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;

  @media (max-width: ${theme.breakpoint.med}px) {
    margin-top: ${toRem(3)};
  }

  p {
    color: ${theme.color.neutral[500]};
  }
`;

const ActionsContainer = styled.div`
  display: flex;

  > * {
    justify-content: flex-end;
  }

  > * > * {
    flex-shrink: 0;
    margin-left: ${toRem(11)};
  }

  @media (max-width: ${theme.breakpoint.med}px) {
    flex-flow: column-reverse;
  }
`;

const PaymentSetInfoLayout = styled.div`
  display: grid;
  width: 100%;
  grid-gap: ${toRem(11)};
  grid-template-columns: repeat(2, 1fr);
  padding: ${toRem(22)} ${toRem(22)} ${toRem(5.5)} ${toRem(22)};

  @media (min-width: ${theme.breakpoint.small}px) {
    grid-template-columns: repeat(auto-fit, minmax(${toRem(150)}, 1fr));
    grid-gap: ${toRem(16.5)};
    justify-items: center;
    margin-bottom: ${toRem(8)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-gap: ${toRem(22)};
    padding-bottom: ${toRem(8.75)};
  }

  > * {
    margin-bottom: 0;
  }
`;

const TotalWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex-grow: 1;
  align-items: center;
  padding: ${toRem(16)};

  button {
    margin-top: ${toRem(4)};
    font-size: ${toRem(12)};
    color: ${theme.color.primary[500]};
  }

  p {
    margin-bottom: ${toRem(12)};
    color: ${theme.color.neutral[500]};
  }
`;

const TipAcceptancePaymentSet = ({ acceptanceBalance, acceptancePaymentSet, makePaymentUrl, isLoading, summaryType }: IAcceptancePaymentSetProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { width: windowWidth } = useWindowSize();

  const calculateTotalByDisbursementMethod = useCallback(
    (method: DisbursementMethod) =>
      acceptancePaymentSet?.summary?.payouts?.reduce((acc, curr) => {
        if (curr.disbursementMethod !== method) return acc;
        return acc + curr.net;
      }, 0),
    [acceptancePaymentSet?.summary?.payouts]
  );

  const onOpen = useCallback(() => {
    if (acceptancePaymentSet?.status === AcceptancePaymentSetStatus.COMPLETED && acceptancePaymentSet.paymentId) {
      dispatch(getCompletedPaymentSetRequest(acceptancePaymentSet.paymentId));
    }
  }, [acceptancePaymentSet]);

  const renderDropdownContent = () => {
    if (acceptancePaymentSet?.error) {
      return (
        <Text size="small" fontWeight="medium" data-cy="acceptance-payment-dropdown-error">
          Unable to get payment details at this time.
        </Text>
      );
    }

    if (summaryType === 'completed') {
      return (
        <>
          <TotalWrapper>
            <Text size="small" fontWeight="medium">
              Acceptance Tips
            </Text>
            {acceptancePaymentSet?.summary ? <H2 fontWeight="medium">{centsToDollarsDisplay(acceptancePaymentSet?.totals.amount)}</H2> : <Skeleton width={97} />}
          </TotalWrapper>
          <TotalWrapper>
            <Text size="small" fontWeight="medium">
              Amount Paid
            </Text>
            {acceptancePaymentSet?.summary ? <H2 fontWeight="medium">{centsToDollarsDisplay(acceptancePaymentSet?.summary?.totals?.gross)}</H2> : <Skeleton width={97} />}
          </TotalWrapper>
          <TotalWrapper>
            <Text size="small" fontWeight="medium">
              Net
            </Text>
            {acceptancePaymentSet?.summary ? <H2 fontWeight="medium">{centsToDollarsDisplay(acceptancePaymentSet?.summary?.totals?.net)}</H2> : <Skeleton width={97} />}
          </TotalWrapper>
          <TotalWrapper>
            <Text size="small" fontWeight="medium">
              Payroll
            </Text>
            {acceptancePaymentSet?.summary ? <H2 fontWeight="medium">{centsToDollarsDisplay(calculateTotalByDisbursementMethod(DisbursementMethod.PAYROLL))}</H2> : <Skeleton width={97} />}
          </TotalWrapper>
          <TotalWrapper>
            <Text size="small" fontWeight="medium">
              Instant
            </Text>
            {acceptancePaymentSet?.summary ? <H2 fontWeight="medium">{centsToDollarsDisplay(calculateTotalByDisbursementMethod(DisbursementMethod.INSTANT))}</H2> : <Skeleton width={97} />}
          </TotalWrapper>
        </>
      );
    }

    return (
      <>
        <TotalWrapper>
          <Text size="small" fontWeight="medium">
            Employees
          </Text>
          <H2 fontWeight="medium">{acceptancePaymentSet?.totals?.employees}</H2>
        </TotalWrapper>
        <TotalWrapper>
          <Text size="small" fontWeight="medium">
            Tips
          </Text>
          <H2 fontWeight="medium">{acceptancePaymentSet?.totals?.payments}</H2>
        </TotalWrapper>
        <TotalWrapper>
          <Text size="small" fontWeight="medium">
            Tip Total
          </Text>
          <H2 fontWeight="medium">{centsToDollarsDisplay(acceptancePaymentSet?.totals?.amount)}</H2>
        </TotalWrapper>
        <TotalWrapper>
          <Text size="small" fontWeight="medium">
            Fees
          </Text>
          <H2 fontWeight="medium">{centsToDollarsDisplay(acceptancePaymentSet?.totals?.fees)}</H2>
        </TotalWrapper>
        <TotalWrapper>
          <Text size="small" fontWeight="medium">
            Net
          </Text>
          <H2 fontWeight="medium">{centsToDollarsDisplay(acceptancePaymentSet?.totals?.net)}</H2>
        </TotalWrapper>
      </>
    );
  };

  return (
    <>
      <AccordionSection
        compact={windowWidth < theme.breakpoint.xsmall}
        open={false}
        isLoading={isLoading}
        onOpen={onOpen}
        title={acceptancePaymentSet && <TipAcceptancePaymentSetHeader acceptancePaymentSet={acceptancePaymentSet} totalAmount={acceptancePaymentSet.totals.amount} windowWidth={windowWidth} />}
        dataCy="acceptance-payment"
        actions={
          acceptancePaymentSet && (
            <ActionsContainer>
              <InfoContainer data-cy="acceptance-payment-info">
                <Text size="xxsmall">
                  <b>{acceptancePaymentSet.status === AcceptancePaymentSetStatus.COMPLETED ? 'Paid:' : 'Last Updated:'}</b> {formatDateForUILongFormat(acceptancePaymentSet.updatedAt, true, false, windowWidth)}
                </Text>
              </InfoContainer>
              <div>
                <Button type="button" size="small" appearance="white" label="Tips Received" onClick={() => history.push(`${routeConstants.private.paymentsAcceptance}/${acceptancePaymentSet.id}/tips`)} />
                {acceptancePaymentSet.status === AcceptancePaymentSetStatus.COMPLETED && acceptancePaymentSet.paymentId && (
                  <Button data-cy="view-payment-set-button" size="small" label="Tips Issued" to={routeForPaymentDetail(acceptancePaymentSet.paymentId)} />
                )}
                {acceptancePaymentSet.status === AcceptancePaymentSetStatus.PENDING && (
                  <>
                    {/*
                    !!! IMPORTANT !!!

                    if any additional click functionality is to be added to this button,
                    be warned that all Cypress tests are currently not actually clicking
                    this button. they are reading thet href value and calling `cy.visit()`
                    instead. this was needed because of the query parameter override
                    of the AllowHoldbacks flag. the query parameter is used to override
                    that flag, but does not work in Cypress when calling its built in `.click()`
                    method. so if any functionality is added to this button in the future,
                    the following test files will need to be updated:

                      - `/cypress/components/payments/acceptance.ts`
                      - `/cypress/e2e/makePayment/tipAcceptance/sideBar.cy.ts`
                      - `/cypress/e2e/makePayment/tipAcceptance/editMode.cy.ts`
                      - `/cypress/e2e/makePayment/tipAcceptance/reviewMode.cy.ts`
                    */}
                    <Button to={makePaymentUrl} size="small" label="Pay Tips" disabled={acceptanceBalance <= 0} tooltipId={`${acceptancePaymentSet.id}_pay_disabled`} data-cy="pay-tips-button" />
                    <PayTipsTooltip id={`${acceptancePaymentSet.id}_pay_disabled`} balance={acceptanceBalance} />
                  </>
                )}
              </div>
            </ActionsContainer>
          )
        }
      >
        <PaymentSetInfoLayout>{renderDropdownContent()}</PaymentSetInfoLayout>
      </AccordionSection>
    </>
  );
};

const HeaderWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
`;

const StyledHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

const Title = styled.div`
  display: flex;
  flex-flow: column nowrap;

  h4 {
    font-size: ${toRem(16)};
    font-weight: ${theme.font.weight.bold};
    margin-right: ${toRem(8)};
  }

  p {
    color: ${theme.color.neutral[500]};
  }
`;

interface IAcceptancePaymentSetHeaderProps extends IAcceptancePaymentSetProps {
  totalAmount?: number;
  windowWidth?: number;
}

const TipAcceptancePaymentSetHeader = ({ acceptancePaymentSet, isLoading, totalAmount, windowWidth }: IAcceptancePaymentSetHeaderProps) => (
  <SkeletonTheme color={theme.color.white} highlightColor={theme.color.white}>
    <HeaderWrapper>
      <StyledHeader>
        <Title>
          {isLoading || !acceptancePaymentSet ? (
            <>
              <Skeleton width={97} />
              <Skeleton width={97} />
            </>
          ) : (
            <>
              <H4>{formatDateForUILongFormat(acceptancePaymentSet.paymentDate, false, true, windowWidth)}</H4>
              <Text size="small">{centsToDollarsDisplay(totalAmount || 0)}</Text>
            </>
          )}
        </Title>
      </StyledHeader>
    </HeaderWrapper>
  </SkeletonTheme>
);

export default TipAcceptancePaymentSet;
